// @ts-nocheck
/*
 * Port of views/dashboard/dashboard-campaign-details.js.
 * API: API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS (single GET). Byte-identical:
 *   - 1-minute refetch cooldown
 *   - CALL_CENTRE_AGENT early return
 *   - Reads response.campaigns
 *   - Last-completed-campaign banner shown only when name is present
 * Chip count preserved: 8 metrics.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Ban,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  List,
  Phone,
  RefreshCw,
} from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { getApiCallShared } from '@/service/sharedGet';
import { API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';

const REFETCH_COOLDOWN_MS = 60 * 1000;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

function MetricChip({ icon, label, value, accentColor }) {
  return (
    <div
      className="flex items-center gap-3 shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]"
      style={{ borderLeftColor: accentColor, borderLeftWidth: 4 }}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
        style={{ background: accentColor }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-lg font-bold text-foreground">{value ?? '—'}</div>
        <div className="truncate text-[11px] font-medium text-muted-foreground">
          {label}
        </div>
      </div>
    </div>
  );
}

export default function DashboardCampaignDetails() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [refetching, setRefetching] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON', e);
        return null;
      }
      if (user.role) setLoggedInUserRole(user.role);
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchDashboardStats();

  }, [loggedInUserRole]);

  const fetchDashboardStats = async () => {
    try {
      const response = await getApiCallShared(API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS);
      setDashboardData(response?.campaigns || {});
      setLastFetchedAt(Date.now());
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const handleRefetch = async () => {
    const elapsed = Date.now() - lastFetchedAt;
    if (elapsed < REFETCH_COOLDOWN_MS) {
      const wait = Math.ceil((REFETCH_COOLDOWN_MS - elapsed) / 1000);
      toast.info(`Please wait ${wait}s before refreshing again`);
      return;
    }
    try {
      setRefetching(true);
      const response = await getApiCall(API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS);
      setDashboardData(response?.campaigns || {});
      setLastFetchedAt(Date.now());
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Error fetching campaign stats');
    } finally {
      setRefetching(false);
    }
  };

  const stats = [
    { label: 'Total Campaigns', value: dashboardData?.totalCampaigns, icon: <List className="h-4 w-4" />, color: '#6366f1' },
    { label: 'This Month', value: dashboardData?.totalCampaignsThisMonth, icon: <BarChart3 className="h-4 w-4" />, color: '#f97316' },
    {
      label: 'Dialed',
      value: dashboardData?.totalProcessed,
      icon: <Phone className="h-4 w-4" style={{ transform: 'rotate(100deg)' }} />,
      color: '#10b981',
    },
    { label: 'Running', value: dashboardData?.runningCampaigns, icon: <Clock className="h-4 w-4" />, color: '#a855f7' },
    { label: 'Errored', value: dashboardData?.erroredCampaigns, icon: <Ban className="h-4 w-4" />, color: '#f43f5e' },
    { label: 'Connected', value: dashboardData?.totalConnected, icon: <CheckCircle2 className="h-4 w-4" />, color: '#0ea5e9' },
    { label: 'Total Numbers', value: dashboardData?.totalNos, icon: <BarChart3 className="h-4 w-4" />, color: '#eab308' },
    { label: 'Completed', value: dashboardData?.completedCampaigns, icon: <BarChart3 className="h-4 w-4" />, color: '#14b8a6' },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">Campaign Details</span>
        <button
          type="button"
          title="Refresh"
          onClick={refetching ? undefined : handleRefetch}
          disabled={refetching}
          className={
            'flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors ' +
            (refetching
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-muted hover:text-foreground')
          }
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (refetching ? 'animate-spin' : '')} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {stats.map((item, i) => (
          <MetricChip
            key={i}
            icon={item.icon}
            label={item.label}
            value={item.value}
            accentColor={item.color}
          />
        ))}
      </div>

      {dashboardData?.lastCompletedCampaignName && (
        <div
          className="mt-4 flex items-center gap-2.5 rounded-lg border-l-4 p-3"
          style={{ borderLeftColor: '#10b981', background: 'rgba(16,185,129,0.08)' }}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: '#10b981' }} />
          <div>
            <div className="text-xs text-muted-foreground">Last Campaign Completed At</div>
            <div className="text-sm font-semibold text-foreground">
              {formatDateTime(dashboardData?.lastCompletedCampaignAt)}
              {dashboardData?.lastCompletedCampaignName ? (
                <span className="font-normal">
                  {' '}
                  — {dashboardData.lastCompletedCampaignName}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
